import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useRowHighlight } from '../utils/useRowHighlight';
import { useAppShell } from '../contexts/AppShellContext';
import { useAuth } from '../contexts/AuthContext';
import { latestReason } from '../data/receiptDraft';
import { getMaterialReceipts, deleteMaterialReceipt } from '../services/materialReceiptService';
import type { MaterialReceiptRow } from '../types/api';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { SortableTh } from '../components/SortableTh';
import { useSort, sortRows } from '../utils/useSort';
import { CATEGORY_FILTERS } from '../utils/jobCategory';
import { windowBounds, inWindow, windowCounts, type ScopeValue } from '../utils/timeWindow';
import { StatusPill } from '../components/StatusPill';
import { RegulatoryIntakeQueue } from '../components/regulatory/RegulatoryIntakeQueue';
import {
  CategoryTag, fmtDate, initials, EditModal, CommentModal,
  ATTENTION_STATUSES, AttentionToggleButton, NeedsAttentionRail,
} from '../components/materialIntakeShared';

const MATERIAL_TABS = [
  { id: 'proxy',      label: 'Proxy, Corp Action & Issuer' },
  { id: 'regulatory', label: 'Regulatory' },
];

const CATEGORY: Record<string, { label: string; cls: string }> = {
  P:  { label: 'Proxy',       cls: 'tp tp-px' },
  CA: { label: 'Corp Action', cls: 'tp tp-ca' },
  R:  { label: 'Regulatory',  cls: 'tp tp-rg' },
  I:  { label: 'Issuer',      cls: 'tp tp-px' },
};

const RECENT_DAYS = 2;

// Filterable material_status values. Excludes 'New' / 'Processing' — those are
// transient system states between camera submit and Pass-2 completion, not a
// disposition an operator would ever filter the queue by.
const STATUS_FILTERS = ['To Verify', 'Matched', 'No Job Found', 'Received Short', 'On Hold'];

// Material / Job # are intentionally omitted: the queue is about "which receipts
// need verification", not "what's this about" — and a matched receipt leaves the
// queue, so Job # is empty for most rows here. Details live one click in (Ace).
const COLUMNS = ['Received', 'Category', 'Scan #', 'Client Name(s)', 'Status', 'User'];

// Sort key per column — raw ISO date so it orders chronologically.
function cellValue(r: MaterialReceiptRow, column: string): string {
  switch (column) {
    case 'Received':        return r.intake_date ?? '';
    case 'Category':        return r.job_category ? (CATEGORY[r.job_category]?.label ?? r.job_category) : '';
    case 'Scan #':          return r.scan_number;
    case 'Client Name(s)':  return r.client_names.join(', ');
    case 'Status':          return r.material_status;
    case 'User':            return r.intake_by ?? '';
    default:                return '';
  }
}


export function IntakeQueuePage() {
  const { setTopbar, materialIntakeTab: materialTab, setMaterialIntakeTab: setMaterialTab } = useAppShell();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const { showToast } = (useOutletContext() as any) || {};
  const author = user?.name?.split(' ')[0] || 'Operator';

  const [rows, setRows]       = useState<MaterialReceiptRow[]>([]);
  const { rowRef, highlightClass } = useRowHighlight();
  const [focus, setFocus]     = useState(false);
  const [categories, setCategories] = useState<Set<string>>(new Set());
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef           = useRef<HTMLDivElement>(null);
  const [statuses, setStatuses] = useState<Set<string>>(new Set());
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef             = useRef<HTMLDivElement>(null);
  const [attentionOpen, setAttentionOpen] = useState(false);
  const [search, setSearch]   = useState('');
  const [scope, setScope]     = useState<ScopeValue>({ window: 'today' });
  const { sort, toggle }      = useSort();
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [editRow, setEditRow] = useState<MaterialReceiptRow | null>(null);
  const [noteRow, setNoteRow] = useState<MaterialReceiptRow | null>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) setCategoryOpen(false);
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) setStatusOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleCategory = useCallback((id: string) => {
    setCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleStatus = useCallback((id: string) => {
    setStatuses(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => getMaterialReceipts()
      .then(r => { if (alive) setRows(r); })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    load();
    const iv = setInterval(load, 3000);  // live: reflect new receipts + status changes
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // This tab is Proxy/CA/Issuer only — Regulatory receipts have their own tab
  // with its own production-pipeline lifecycle (see regulatoryRows below), so
  // every metric/list below (stats, Needs Attention, the queue itself) is
  // scoped to non-Regulatory rows.
  const proxyRows = useMemo(() => rows.filter(r => r.job_category !== 'R'), [rows]);

  // Time window is the primary axis — applied before the queue predicate.
  const bounds = useMemo(() => windowBounds(scope), [scope]);
  const windowedRows = useMemo(() => proxyRows.filter(r => inWindow(r.intake_date, bounds)), [proxyRows, bounds]);
  const winCounts = useMemo(() => windowCounts(proxyRows), [proxyRows]);

  // Intake queue = everything not yet resolved, plus matched receipts from the last 48h.
  const queue = useMemo(() => {
    const cutoff = new Date(Date.now() - RECENT_DAYS * 86400000).toISOString().split('T')[0];
    return windowedRows.filter(r => r.material_status !== 'Matched' || (r.intake_date ?? '') >= cutoff);
  }, [windowedRows]);

  // Regulatory tab's own dataset — unfiltered by the time window/48h cutoff,
  // since a Matched regulatory row tracks a multi-day production pipeline
  // (Ready to Mail -> Completed), not a receive-and-verify step. Unmatched
  // rows live in that tab's own "To Verify" sub-tab instead of here.
  const regulatoryRows = useMemo(() => rows.filter(r => r.job_category === 'R'), [rows]);

  useEffect(() => {
    setTopbar('Material Intake', materialTab === 'regulatory' ? 'Regulatory Cell intake queue' : `${queue.length} scans in queue`);
  }, [setTopbar, queue.length, materialTab]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const data = queue.filter(r => {
      if (focus && r.material_status !== 'To Verify') return false;
      if (categories.size > 0 && !categories.has(r.job_category ?? '')) return false;
      if (statuses.size > 0 && !statuses.has(r.material_status)) return false;
      if (q && !`${r.client_names.join(' ')} ${r.scan_number}`.toLowerCase().includes(q)) return false;
      return true;
    });
    return sortRows(data, sort, cellValue);
  }, [queue, focus, categories, statuses, search, sort]);

  // Needs Attention rail — every unresolved exception, newest first. Pinned
  // across the WHOLE dataset (not the time scope or table filters) so an open
  // exception is never hidden by switching to Today / a status pill.
  const attention = useMemo(() => (
    proxyRows
      .filter(r => ATTENTION_STATUSES.includes(r.material_status))
      .sort((a, b) => (b.intake_date ?? '').localeCompare(a.intake_date ?? ''))
  ), [proxyRows]);

  const toVerifyCount = useMemo(() => queue.filter(r => r.material_status === 'To Verify').length, [queue]);

  // Stat banner — monitoring metrics over all receipts in the active window.
  const stats = useMemo(() => ({
    intakes:    windowedRows.length,
    matched:    windowedRows.filter(r => r.material_status === 'Matched').length,
    exceptions: windowedRows.filter(r => ['No Job Found', 'Received Short', 'On Hold'].includes(r.material_status)).length,
  }), [windowedRows]);

  const onSaved = useCallback((u: MaterialReceiptRow) => setRows(prev => prev.map(r => r.id === u.id ? u : r)), []);
  const openAce = useCallback((r: MaterialReceiptRow) => navigate('/ace', { state: { receiptThread: { receiptId: r.id, scan: r.scan_number, client: r.client_names.join(', '), row: r } } }), [navigate]);
  const onDelete = useCallback(async (r: MaterialReceiptRow) => {
    try { await deleteMaterialReceipt(r.id); setRows(prev => prev.filter(x => x.id !== r.id)); showToast?.('Receipt deleted'); }
    catch { showToast?.('Failed to delete'); }
  }, [showToast]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Material-type tabs — styled like the Receiving Log tabs. */}
      <div className="flex gap-0 px-6 border-b border-trim flex-shrink-0">
        {MATERIAL_TABS.map(t => (
          <button key={t.id} onClick={() => setMaterialTab(t.id as 'proxy' | 'regulatory')}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
            style={{ borderColor: materialTab === t.id ? '#2D7575' : 'transparent', color: materialTab === t.id ? '#256060' : '#73747A' }}>
            {t.label}
            <span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{ background: materialTab === t.id ? '#2D7575' : '#F5F7FA', color: materialTab === t.id ? 'white' : '#73747A' }}>
              {t.id === 'proxy' ? queue.length : regulatoryRows.length}
            </span>
          </button>
        ))}
      </div>

      {materialTab === 'regulatory' ? (
        <RegulatoryIntakeQueue rows={regulatoryRows} onSaved={onSaved} openAce={openAce} showToast={showToast} />
      ) : error ? (
        <div className="px-6 py-5 text-sm text-red-600">{error}</div>
      ) : (<>
      {/* Global header — period + scoped stats on a recessed band, full width
          so the band runs edge-to-edge above both the table and the rail */}
      <PageHeader
        stats={[
          { label: 'Intakes', value: stats.intakes },
          { label: 'Matched', value: stats.matched, accent: true },
          { label: 'Exceptions', value: stats.exceptions },
        ]}
        scope={scope}
        onScopeChange={setScope}
        counts={winCounts}
      />

      {/* Filter row — category dropdown, then the Focus-mode toggle, then search. */}
      <div className="flex items-center gap-3 px-6 pt-4 pb-4 border-b border-trim flex-shrink-0 flex-wrap gap-y-2">
        <div className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#F5F7FA', color: '#595A60' }}>{filtered.length} scans</div>
        <div className="relative" ref={categoryRef}>
          <button
            onClick={() => setCategoryOpen(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-trim text-xs font-medium text-copy hover:bg-wash transition-colors"
          >
            Category
            {categories.size > 0 && (
              <span className="px-1.5 rounded-full text-xs font-semibold tabular-nums" style={{ background: '#F1F5F9', color: '#73747A' }}>{categories.size}</span>
            )}
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={`transition-transform ${categoryOpen ? 'rotate-180' : ''}`}>
              <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {categoryOpen && (
            <div className="absolute top-[calc(100%+6px)] left-0 bg-white border border-trim rounded-xl overflow-hidden z-50" style={{ minWidth: 180, boxShadow: '0 8px 24px rgba(4,50,67,0.12)' }}>
              {CATEGORY_FILTERS.filter(c => c.id !== 'All' && c.id !== 'R').map(c => (
                <label key={c.id} className="flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-ink hover:bg-wash cursor-pointer">
                  <input type="checkbox" checked={categories.has(c.id)} onChange={() => toggleCategory(c.id)} className="w-3.5 h-3.5 rounded border-trim accent-teal-500" />
                  {c.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="relative" ref={statusRef}>
          <button
            onClick={() => setStatusOpen(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-trim text-xs font-medium text-copy hover:bg-wash transition-colors"
          >
            Status
            {statuses.size > 0 && (
              <span className="px-1.5 rounded-full text-xs font-semibold tabular-nums" style={{ background: '#F1F5F9', color: '#73747A' }}>{statuses.size}</span>
            )}
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={`transition-transform ${statusOpen ? 'rotate-180' : ''}`}>
              <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {statusOpen && (
            <div className="absolute top-[calc(100%+6px)] left-0 bg-white border border-trim rounded-xl overflow-hidden z-50" style={{ minWidth: 180, boxShadow: '0 8px 24px rgba(4,50,67,0.12)' }}>
              {STATUS_FILTERS.map(s => (
                <label key={s} className="flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-ink hover:bg-wash cursor-pointer">
                  <input type="checkbox" checked={statuses.has(s)} onChange={() => toggleStatus(s)} className="w-3.5 h-3.5 rounded border-trim accent-teal-500" />
                  {s}
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="w-px h-5 bg-trim" />
        {/* Focus mode — narrows the queue to just what needs verifying */}
        <button
          onClick={() => setFocus(f => !f)}
          role="switch"
          aria-checked={focus}
          title="Focus — show only receipts that need verification"
          className="flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-full text-xs font-medium border transition-colors"
          style={{ borderColor: focus ? '#2D7575' : '#E5E7EB', background: focus ? '#E6F4F4' : 'white', color: focus ? '#256060' : '#595A60' }}
        >
          <span className="relative inline-flex flex-shrink-0" style={{ width: 26, height: 15 }}>
            <span className="absolute inset-0 rounded-full transition-colors" style={{ background: focus ? '#2D7575' : '#CBD5E1' }} />
            <span className="absolute top-0.5 rounded-full bg-white shadow-sm transition-all" style={{ width: 11, height: 11, left: focus ? 13 : 2 }} />
          </span>
          Focus
          {toVerifyCount > 0 && (
            <span className="px-1.5 rounded-full text-xs font-semibold tabular-nums" style={{ background: focus ? '#FFFFFF' : '#F1F5F9', color: focus ? '#0F6E56' : '#73747A' }}>{toVerifyCount}</span>
          )}
        </button>
        <div className="ml-auto">
          <SearchInput value={search} onChange={setSearch} placeholder="Search scans, clients…" width={220} />
        </div>
        {!attentionOpen && <AttentionToggleButton count={attention.length} onClick={() => setAttentionOpen(true)} />}
      </div>

      {/* Below the band: working table on the left, exceptions rail on the right */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
      <div className="flex-1 overflow-auto px-6 pb-6">
        {loading && <div className="text-sm text-muted py-4">Loading…</div>}
        <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
          <thead>
            <tr>
              {COLUMNS.map(h => (
                <SortableTh key={h} label={h} column={h} sort={sort} onSort={toggle} />
              ))}
              <SortableTh label="Actions" sort={sort} onSort={toggle} />
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} ref={rowRef(r.id)} className={`group hover:bg-wash transition-colors cursor-pointer ${highlightClass(r.id)}`} onClick={() => openAce(r)}>
                <td className="px-2 py-1.5 border-b border-wash text-xs text-muted whitespace-nowrap align-top">
                  {fmtDate(r.intake_date)}{r.intake_time ? <span className="text-muted"> · {r.intake_time}</span> : ''}
                </td>
                <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top"><CategoryTag category={r.job_category} /></td>
                <td className="px-2 py-1.5 border-b border-wash text-xs font-medium text-ink whitespace-nowrap align-top">{r.scan_number}</td>
                <td className="px-2 py-1.5 border-b border-wash align-top">
                  <div className="max-w-[240px] text-xs text-copy break-words" title={r.client_names.join(', ')}>{r.client_names.join(', ')}</div>
                </td>
                <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top">
                  <StatusPill status={r.material_status} />
                </td>
                <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-tint text-xs font-semibold text-ink uppercase" title={r.intake_by ?? ''}>{initials(r.intake_by)}</span>
                </td>
                <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-0.5">
                    <button onClick={() => setEditRow(r)} title="Edit" className="w-7 h-7 rounded flex items-center justify-center text-copy hover:bg-wash hover:text-ink">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    </button>
                    <button onClick={() => setNoteRow(r)} title="Exception note" className="w-7 h-7 rounded flex items-center justify-center relative text-copy hover:bg-wash hover:text-ink">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                      {latestReason(r.note_log) && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-500" />}
                    </button>
                    {can('receipt.delete') && (
                      <button onClick={() => onDelete(r)} title="Delete" className="w-7 h-7 rounded flex items-center justify-center text-danger hover:bg-danger-bg">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-muted text-sm">No scans in the queue match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </div>

        {/* Needs Attention — collapsible rail of every open exception. Collapsed
            by default; the toolbar warning icon reopens it. */}
        {attentionOpen && (
          <NeedsAttentionRail rows={attention} onOpen={openAce} onClose={() => setAttentionOpen(false)} />
        )}
      </div>

      {editRow && <EditModal row={editRow} onClose={() => setEditRow(null)} onSaved={onSaved} showToast={showToast} />}
      {noteRow && <CommentModal row={noteRow} author={author} onClose={() => setNoteRow(null)} onSaved={onSaved} showToast={showToast} />}
      </>)}
    </div>
  );
}
